import { Box, Divider, Flex, Grid, Text } from "@chakra-ui/react";
import GameSearchFilter from "./GameSearchFilter";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import GameItem from "../../GameItem/GameItem";
import { languageTransfer } from "../../../utils/languageTransfer";
import i18next from "i18next";
import NotFound from "../../ProgressBlock/NotFound";
import { useTranslation } from "react-i18next";

const GameSearchContent = ({ onClose = () => {} } = {}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const gamesInfo = useSelector((state) => state.isHandleAllGame);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState(null);
  const [highlightUid, setHighlightUid] = useState(null);

  const gameList = useMemo(() => {
    const {
      newGameList = [],
      topGameList = [],
      allGames = [],
      favoriteGameList = [],
      randomGameList = [],
    } = gamesInfo || {};

    let baseList = topGameList;

    if (filterCategory) {
      let targetList = [];
      switch (filterCategory) {
        case "new":
          targetList = newGameList;
          break;
        case "top":
          targetList = topGameList;
          break;
        case "favorite":
          targetList = favoriteGameList;
          break;
        case "random":
          targetList = randomGameList;
          break;
        default:
          targetList = [];
      }
      const targetUids = new Set(targetList?.map((g) => g.gameUid));
      baseList = baseList?.filter((g) => targetUids.has(g.gameUid));
    }

    if (searchQuery && searchQuery.trim().length >= 2) {
      const lowerQuery = searchQuery.toLowerCase();
      return allGames?.filter((g) => {
        const combinedNames = `${g.gameNameEn}${g.gameNameTw}${g.gameNameVi}${g.gameNameZh}`;
        return combinedNames.toLowerCase().includes(lowerQuery);
      });
    }

    return baseList;
  }, [gamesInfo, filterCategory, searchQuery]);

  // 捲軸恢復與點擊標跡邏輯
  useEffect(() => {
    const savedUid = sessionStorage.getItem("lastClickedGameUid");

    if (gameList?.length > 0) {
      if (savedUid) {
        setHighlightUid(savedUid);

        // 使用 setTimeout 確保資料已渲染至 DOM
        const timer = setTimeout(() => {
          const targetElement = document.getElementById(
            `search-item-${savedUid}`,
          );
          if (targetElement) {
            targetElement.scrollIntoView({
              behavior: "auto",
              block: "center",
            });
          }
          sessionStorage.removeItem("lastClickedGameUid");
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [gameList]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterCategory(null);
    setIsFilterOpen(false);
  };

  return (
    <Flex direction="column" h="100%" overflow="hidden">
      <GameSearchFilter
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        gamesInfo={gamesInfo}
        filterCategory={filterCategory}
        setFilterCategory={setFilterCategory}
        onClearFilters={handleClearFilters}
      />
      <Divider />

      {gameList?.length === 0 ? (
        <Box flex={1}>
          <NotFound />
        </Box>
      ) : (
        <Box
          flex={1}
          w="100%"
          overflowY="auto"
          pr={2}
          sx={{
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              background: "brand.500",
              borderRadius: "10px",
            },
          }}
        >
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              xl: "repeat(3, 1fr)",
            }}
            gap={2}
            py={4}
          >
            {gameList?.map((game, i) => {
              const gameName =
                game?.[
                  `gameName${languageTransfer({ lang: i18next.language })}` ||
                    "gameNameEn"
                ];

              const isHighlighted = highlightUid === game.gameUid;

              return (
                <Box
                  key={game.gameUid || game.uid || i}
                  id={`search-item-${game.gameUid}`}
                  mb={{
                    base: 0,
                    md: 5,
                  }}
                  position="relative"
                  onMouseEnter={() => {
                    if (isHighlighted) setHighlightUid(null);
                  }}
                >
                  <Box position="relative">
                    <GameItem
                      gameProps={{
                        gameName,
                        platform: game?.game_platform,
                        gameCode: game?.gameCode,
                        gameType: game?.gameType,
                        gameNameEn: game?.gameNameEn,
                        gameSubType: game?.game_subtype,
                      }}
                      onCloseFunction={() => {
                        sessionStorage.setItem(
                          "lastClickedGameUid",
                          game.gameUid,
                        );
                        onClose();
                      }}
                      originProps={game}
                    />
                    {isHighlighted && (
                      <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="rgba(0,0,0,0.6)"
                        borderRadius="15px"
                        zIndex="10"
                        alignItems="center"
                        justifyContent="center"
                        pointerEvents="none"
                        backdropFilter="blur(2px)"
                        border="2px solid"
                        borderColor="brand.400"
                      >
                        <Text
                          color="white"
                          fontSize="14px"
                          fontWeight="700"
                          textShadow="0px 0px 4px rgba(0,0,0,0.8)"
                        >
                          {i18n("lastPlayed") || "LAST VIEWED"}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Grid>
        </Box>
      )}
    </Flex>
  );
};

export default GameSearchContent;
